import type { ClassAnalyticsResponse } from '@study-decks/shared'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class AnalyticsApiError extends Error {
  override readonly name = 'AnalyticsApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

type TokenProvider = () => Promise<string>

async function requestAnalyticsApi<T>(
  path: string,
  getIdToken: TokenProvider,
  init?: RequestInit,
): Promise<T> {
  const idToken = await getIdToken()
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${idToken}`,
      ...init?.headers,
    },
  })

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    const code = errorBody.error?.code ?? 'REQUEST_FAILED'
    const message = errorBody.error?.message ?? `Request failed (${response.status})`

    if (response.status === 403) {
      throw new AnalyticsApiError('FORBIDDEN', "You're not a teacher of this class.")
    }
    if (response.status === 404) {
      throw new AnalyticsApiError('NOT_FOUND', 'Class not found.')
    }
    throw new AnalyticsApiError(code, message)
  }

  return (await response.json()) as T
}

export async function getClassAnalytics(
  getIdToken: TokenProvider,
  classId: string,
  options?: { refresh?: boolean },
): Promise<{ summary: ClassAnalyticsResponse; cached: boolean }> {
  const query = options?.refresh ? '?refresh=1' : ''
  return requestAnalyticsApi(`/api/analytics/class/${classId}${query}`, getIdToken)
}
